// @givmo/social — the composer's state and what it derives (SMM-WEB1; §SMM-8 point 2, "per-platform
// variant review cards with mirrored caps and counters and honest constraint hints"). Pure: the
// page holds the state, this file says what each chosen account would receive and whether the post
// can go, and what the write sends.
//
// One base text, optional link and image, and the accounts it goes to. Each chosen account's post
// is the base FITTED to its platform (`fittedText`, the server's own derivation) until a person
// edits it for that account; an edited account's text is sent as its own (`SocialChannelIn.text`),
// an unedited one is left for the server to derive, so the two can never disagree.

import type {
  OrgIntegrationConnectionOut,
  SocialImageOut,
  SocialPostOut,
  SocialPostWriteIn,
} from '@givmo/api-client';
import {
  IMAGE_REQUIRED_PLATFORMS,
  MAX_BASE_TEXT_LENGTH,
  MAX_CHANNELS,
  charCount,
  fittedText,
  linkProblem,
  linkSentApart,
  platformLabel,
  textCap,
  textRoom,
  variantProblem,
  type VariantProblem,
} from './social-platforms';

/** The Email Campaign rental's tag, and what its entry offered (§SMM-8, the Email Campaign entry). */
export interface ComposerContext {
  kind: string;
  ref: string;
  /** The campaign's name, as the entry handed it; never read from the wire. */
  label: string | null;
}

export interface ComposerState {
  text: string;
  link: string;
  image: SocialImageOut | null;
  /** Chosen accounts, by connection id, in the order they were chosen. */
  channels: string[];
  /** An account's own text, by connection id, once a person edits it for that account. */
  overrides: Readonly<Record<string, string>>;
  context: ComposerContext | null;
}

export const EMPTY_COMPOSER: ComposerState = {
  text: '',
  link: '',
  image: null,
  channels: [],
  overrides: {},
  context: null,
};

/** An account a post may go to: a connected row of the store's purpose. */
export interface Channel {
  connectionId: string;
  platform: string;
  name: string;
  needsReconnect: boolean;
}

export function channelsOf(rows: readonly OrgIntegrationConnectionOut[]): Channel[] {
  return rows
    .filter((row) => row.status === 'connected' || row.status === 'needs_reconnect')
    .map((row) => ({
      connectionId: row.connection_id,
      platform: row.provider,
      name: row.account_name?.trim() || platformLabel(row.provider),
      needsReconnect: row.status === 'needs_reconnect',
    }));
}

/** One chosen account's post, as the composer shows it before anything is sent. */
export interface VariantView {
  channel: Channel;
  text: string;
  customized: boolean;
  /** Characters used and the room the platform has, the link counted where the send adds it. */
  used: number;
  room: number;
  /** What the person sees counted: everything the platform receives (the link included where
   *  the send adds it to the text) against the platform's own limit, the same number everywhere. */
  sent: number;
  cap: number;
  problem: VariantProblem | null;
  /** How the link travels: apart (a preview), at the end of the text, or not at all. */
  link: 'apart' | 'appended' | 'none';
  imageRequired: boolean;
}

function trimmedLink(state: ComposerState): string | null {
  const link = state.link.trim();
  return link.length > 0 && linkProblem(link) === null ? link : null;
}

export function variantViews(state: ComposerState, channels: readonly Channel[]): VariantView[] {
  const link = trimmedLink(state);
  const hasImage = state.image !== null;
  const shape = state.image
    ? { width: state.image.width ?? null, height: state.image.height ?? null }
    : null;
  const views: VariantView[] = [];
  for (const id of state.channels) {
    const channel = channels.find((entry) => entry.connectionId === id);
    if (!channel) continue;
    const own = state.overrides[id];
    const customized = own !== undefined;
    const text = customized ? own : fittedText(channel.platform, state.text, link, hasImage);
    const appended = link !== null && !linkSentApart(channel.platform, hasImage);
    views.push({
      channel,
      text,
      customized,
      used: charCount(text),
      room: textRoom(channel.platform, link, hasImage),
      sent: charCount(text) + (appended && link !== null ? charCount(link) + 2 : 0),
      cap: textCap(channel.platform),
      problem: variantProblem(channel.platform, text, link, shape),
      link:
        link === null ? 'none' : linkSentApart(channel.platform, hasImage) ? 'apart' : 'appended',
      imageRequired: IMAGE_REQUIRED_PLATFORMS.has(channel.platform),
    });
  }
  return views;
}

/** What stops the post from being saved or sent, in the order a person would fix it. */
export type ComposerBlock =
  | 'no_text'
  | 'text_too_long'
  | 'bad_link'
  | 'no_channels'
  | 'too_many_channels'
  | 'variant';

export function composerBlocks(
  state: ComposerState,
  views: readonly VariantView[],
): ComposerBlock[] {
  const blocks: ComposerBlock[] = [];
  if (state.text.trim().length === 0) blocks.push('no_text');
  if (charCount(state.text) > MAX_BASE_TEXT_LENGTH) blocks.push('text_too_long');
  if (linkProblem(state.link) !== null) blocks.push('bad_link');
  if (state.channels.length === 0) blocks.push('no_channels');
  if (state.channels.length > MAX_CHANNELS) blocks.push('too_many_channels');
  if (views.some((view) => view.problem !== null)) blocks.push('variant');
  return blocks;
}

/**
 * Why the post can't be saved, scheduled or published yet, as one thing to do — the first block a
 * person would fix — or null when it can go. Each variant's own rule is named by its platform, with
 * the way out ("or untick Instagram"), since a post goes to every chosen account or to none.
 */
export type BlockReason =
  | { code: 'no_text' }
  | { code: 'base_too_long' }
  | { code: 'bad_link' }
  | { code: 'no_channels' }
  | { code: 'too_many_channels' }
  | { code: 'variant'; platform: string; problem: VariantProblem['code'] };

export function blockReason(
  blocks: readonly ComposerBlock[],
  views: readonly VariantView[],
): BlockReason | null {
  if (blocks.includes('no_text')) return { code: 'no_text' };
  if (blocks.includes('text_too_long')) return { code: 'base_too_long' };
  if (blocks.includes('bad_link')) return { code: 'bad_link' };
  if (blocks.includes('no_channels')) return { code: 'no_channels' };
  if (blocks.includes('too_many_channels')) return { code: 'too_many_channels' };
  const blocked = views.find((view) => view.problem !== null);
  if (blocked?.problem) {
    return { code: 'variant', platform: blocked.channel.platform, problem: blocked.problem.code };
  }
  return null;
}

/** The write's body: the whole composition, an edited account's own text, the context. */
export function writeBody(state: ComposerState): SocialPostWriteIn {
  const link = state.link.trim();
  return {
    text: state.text,
    link_url: link.length > 0 ? link : null,
    image_id: state.image?.image_id ?? null,
    channels: state.channels.map((id) =>
      state.overrides[id] !== undefined
        ? { connection_id: id, text: state.overrides[id] }
        : { connection_id: id },
    ),
    ...(state.context ? { context: { kind: state.context.kind, ref: state.context.ref } } : {}),
  };
}

/**
 * A saved draft back in the composer: its base, link, image and accounts, and each account's
 * text kept as its own only where it is not the base fitted to its platform (which the server
 * derived, and would derive again).
 */
export function stateFromPost(post: SocialPostOut, label: string | null = null): ComposerState {
  const link = post.link_url ?? null;
  const hasImage = post.image != null;
  const overrides: Record<string, string> = {};
  const channels: string[] = [];
  for (const variant of post.variants ?? []) {
    const id = variant.connection_id;
    if (!id) continue;
    channels.push(id);
    if (variant.text !== fittedText(variant.platform, post.text, link, hasImage)) {
      overrides[id] = variant.text;
    }
  }
  return {
    text: post.text,
    link: link ?? '',
    image: post.image ?? null,
    channels,
    overrides,
    context: post.context
      ? { kind: post.context.kind, ref: String(post.context.ref), label }
      : null,
  };
}
